import { Inline, xcss } from '@atlaskit/primitives';
import { PluginKey } from '@databases/plugins';
import { isPluginEnabled } from '@databases/plugins/helper';
import { type DatabaseView } from '@databases/types/database/view';
import useHandleOutsideClick from '@databases/utils/hooks/useHandleOutsideClick';
import { getLayoutIcons } from '@databases/view-settings/utils';
import { type ViewPanelSettingType } from '@databases/views/types';
import React, { type FunctionComponent, useCallback, useState } from 'react';

import ViewActions, { type ReorderingProps } from './ViewActions';
import ViewNameWithTruncation from './ViewNameWithTruncation';

const TabInlineStyles = xcss({
  paddingLeft: 'space.075',
  paddingRight: 'space.100',
  alignItems: 'center',
});

const ActiveTabStyles = xcss({
  paddingRight: 'space.0',
});

type ViewTabContentProps = {
  contentId: string;
  view: DatabaseView;
  isActive: boolean;
  isDefault: boolean;
  isReadonly: boolean;
  openViewSettingsPanel: (activeViewSetting: ViewPanelSettingType | null) => void;
  showViewSettings?: boolean;
  isUnsavedView?: boolean;
  isDragging?: boolean;
  reorderingProps?: ReorderingProps;
};

export const ViewTabContent: FunctionComponent<ViewTabContentProps> = ({
  contentId,
  view,
  isActive,
  isDefault,
  isReadonly,
  openViewSettingsPanel,
  showViewSettings = true,
  isUnsavedView,
  isDragging = false,
  reorderingProps,
}) => {
  const [isViewActionsOpen, setIsViewActionsOpen] = useState(false);
  const showViewSettingAction = isPluginEnabled(PluginKey.VIEW_SETTINGS_ACTIONS);

  useHandleOutsideClick(() => setIsViewActionsOpen(false), undefined, [
    '[data-testid="view-actions-dropdown-menu--content"]',
    '[data-testid="view-actions-dropdown-menu--trigger"]',
  ]);

  const LayoutIcon = useCallback(
    () =>
      getLayoutIcons({
        type: view.settings.layout.type,
        iconSize: 'medium',
        isDefault,
        isPresentationOnly: false,
        isUnsavedView,
      }).icon,
    [isDefault, isUnsavedView, view.settings.layout.type]
  );

  return (
    <Inline
      xcss={[
        TabInlineStyles,
        showViewSettingAction && showViewSettings && isActive && ActiveTabStyles,
      ]}
      space="space.100"
    >
      <LayoutIcon />
      <ViewNameWithTruncation view={view} isDragging={isDragging} />
      {showViewSettings && showViewSettingAction && isActive && (
        <ViewActions
          contentId={contentId}
          view={view}
          isViewActive={isActive}
          isOpen={isViewActionsOpen}
          setIsViewActionsOpen={setIsViewActionsOpen}
          isDefault={isDefault}
          isReadonly={isReadonly}
          openViewSettingsPanel={openViewSettingsPanel}
          isTabMode
          isUnsavedView={isUnsavedView}
          reorderingProps={reorderingProps}
        />
      )}
    </Inline>
  );
};
